import { SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Checkbox,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useState, useMemo, useRef, useEffect } from "react";
import { FeatureFlag } from "../../api/feature-flags";
import { CustomerMap } from "../../domain/customer";
import { CHANGE_TYPE_ADD, CHANGE_TYPE_REMOVE, StagedChange } from "../../domain/feature-flags";

export type InlineChangesetEditorProps = {
  featureFlags: Record<string, FeatureFlag>;
  customerMap: CustomerMap;
  changes: StagedChange[];
  onToggleChange: (change: StagedChange) => void;
};

export function InlineChangesetEditor({
  featureFlags,
  customerMap,
  changes,
  onToggleChange,
}: InlineChangesetEditorProps) {
  const [selectedFlag, setSelectedFlag] = useState("");
  const [flagSearchQuery, setFlagSearchQuery] = useState("");
  const [isFlagDropdownOpen, setIsFlagDropdownOpen] = useState(false);
  const [highlightedFlagIndex, setHighlightedFlagIndex] = useState(0);
  const [customerSearchQuery, setCustomerSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get all available customers from the customer map
  const allCustomers = useMemo(() => {
    const customers: Array<{ cxId: string; name: string }> = [];

    Object.entries(customerMap).forEach(([cxId, entry]) => {
      if (entry?.rootCustomer.email) {
        customers.push({ cxId, name: entry.rootCustomer.email });
      }
    });

    return customers.sort((a, b) => a.name.localeCompare(b.name));
  }, [customerMap]);

  // Filter customers based on search
  const filteredCustomers = useMemo(() => {
    if (!customerSearchQuery.trim()) return allCustomers;
    const query = customerSearchQuery.toLowerCase();
    return allCustomers.filter(
      customer =>
        customer.name.toLowerCase().includes(query) || customer.cxId.toLowerCase().includes(query)
    );
  }, [allCustomers, customerSearchQuery]);

  // Get flag names that have values arrays (not global-only flags)
  const flagNames = useMemo(() => {
    return Object.entries(featureFlags)
      .filter(([, flag]) => flag.values !== undefined)
      .map(([name]) => name)
      .sort();
  }, [featureFlags]);

  // Filter flag names based on search
  const filteredFlagNames = useMemo(() => {
    if (!flagSearchQuery.trim()) return flagNames;
    const query = flagSearchQuery.toLowerCase();
    return flagNames.filter(name => name.toLowerCase().includes(query));
  }, [flagNames, flagSearchQuery]);

  // Reset highlighted index when filtered list changes
  useEffect(() => {
    setHighlightedFlagIndex(0);
  }, [filteredFlagNames.length]);

  // Check if a customer is currently enabled for the selected flag (in current state + changes)
  function isCustomerEnabled(cxId: string): boolean {
    if (!selectedFlag) return false;

    const flag = featureFlags[selectedFlag];
    const currentlyEnabled = flag?.values?.includes(cxId) ?? false;

    // Check if there's a pending change that modifies this
    const pendingAdd = changes.some(
      c => c.flagName === selectedFlag && c.cxId === cxId && c.type === CHANGE_TYPE_ADD
    );
    const pendingRemove = changes.some(
      c => c.flagName === selectedFlag && c.cxId === cxId && c.type === CHANGE_TYPE_REMOVE
    );

    if (pendingAdd) return true;
    if (pendingRemove) return false;
    return currentlyEnabled;
  }

  // Check if customer state differs from original
  function getCustomerChangeState(cxId: string): "addition" | "removal" | "unchanged" {
    if (!selectedFlag) return "unchanged";

    const flag = featureFlags[selectedFlag];
    const originallyEnabled = flag?.values?.includes(cxId) ?? false;
    const currentlyEnabled = isCustomerEnabled(cxId);

    if (currentlyEnabled && !originallyEnabled) return "addition";
    if (!currentlyEnabled && originallyEnabled) return "removal";
    return "unchanged";
  }

  function scrollToHighlighted(index: number) {
    if (dropdownRef.current) {
      const items = dropdownRef.current.children;
      if (items[index]) {
        (items[index] as HTMLElement).scrollIntoView({
          block: "nearest",
        });
      }
    }
  }

  function handleFlagKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!isFlagDropdownOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        setIsFlagDropdownOpen(true);
        return;
      }
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedFlagIndex(prev => {
          const next = Math.min(prev + 1, filteredFlagNames.length - 1);
          scrollToHighlighted(next);
          return next;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedFlagIndex(prev => {
          const next = Math.max(prev - 1, 0);
          scrollToHighlighted(next);
          return next;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (filteredFlagNames[highlightedFlagIndex]) {
          selectFlag(filteredFlagNames[highlightedFlagIndex]);
        }
        break;
      case "Escape":
        setIsFlagDropdownOpen(false);
        break;
    }
  }

  function selectFlag(flagName: string) {
    setSelectedFlag(flagName);
    setFlagSearchQuery(flagName);
    setIsFlagDropdownOpen(false);
  }

  function handleCustomerToggle(cxId: string) {
    if (!selectedFlag) return;

    if (isCustomerEnabled(cxId)) {
      // Currently enabled, want to disable - stage a removal
      onToggleChange({ type: CHANGE_TYPE_REMOVE, flagName: selectedFlag, cxId });
    } else {
      // Currently disabled, want to enable - stage an addition
      onToggleChange({ type: CHANGE_TYPE_ADD, flagName: selectedFlag, cxId });
    }
  }

  // Count changes for selected flag
  const flagChangeCount = useMemo(() => {
    if (!selectedFlag) return { additions: 0, removals: 0 };
    const additions = changes.filter(
      c => c.flagName === selectedFlag && c.type === CHANGE_TYPE_ADD
    ).length;
    const removals = changes.filter(
      c => c.flagName === selectedFlag && c.type === CHANGE_TYPE_REMOVE
    ).length;
    return { additions, removals };
  }, [changes, selectedFlag]);

  return (
    <VStack align="stretch" gap={3} h="full">
      {/* Flag selector */}
      <Box position="relative">
        <Text mb={2} fontSize="sm" fontWeight="medium" color="gray.600">
          1. Select Feature Flag
        </Text>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search feature flags..."
            value={flagSearchQuery}
            onChange={e => {
              setFlagSearchQuery(e.target.value);
              setSelectedFlag("");
              setIsFlagDropdownOpen(true);
            }}
            onFocus={() => setIsFlagDropdownOpen(true)}
            onBlur={() => {
              setTimeout(() => setIsFlagDropdownOpen(false), 150);
            }}
            onKeyDown={handleFlagKeyDown}
            bg="white"
            size="sm"
          />
        </InputGroup>

        {/* Dropdown overlays content - uses position absolute */}
        {isFlagDropdownOpen && (
          <Box
            ref={dropdownRef}
            position="absolute"
            top="100%"
            left={0}
            right={0}
            mt={1}
            bg="white"
            borderWidth="1px"
            borderRadius="md"
            boxShadow="lg"
            maxH="200px"
            overflowY="auto"
            zIndex={20}
          >
            {filteredFlagNames.length === 0 ? (
              <Text color="gray.500" fontSize="sm" p={3}>
                No flags found
              </Text>
            ) : (
              filteredFlagNames.map((name, index) => (
                <Box
                  key={name}
                  px={3}
                  py={2}
                  cursor="pointer"
                  _hover={{ bg: "gray.100" }}
                  onClick={() => selectFlag(name)}
                  bg={
                    index === highlightedFlagIndex
                      ? "blue.100"
                      : selectedFlag === name
                      ? "blue.50"
                      : undefined
                  }
                >
                  <Text fontSize="sm">{name}</Text>
                </Box>
              ))
            )}
          </Box>
        )}
      </Box>

      {/* Customer selector */}
      <Box flex={1} opacity={selectedFlag ? 1 : 0.5}>
        <Flex mb={2} justifyContent="space-between" alignItems="center">
          <Text fontSize="sm" fontWeight="medium" color="gray.600">
            2. Toggle Customers
          </Text>
          {selectedFlag && (flagChangeCount.additions > 0 || flagChangeCount.removals > 0) && (
            <Text fontSize="xs" color="blue.600" fontWeight="medium">
              {flagChangeCount.additions > 0 && `+${flagChangeCount.additions}`}
              {flagChangeCount.additions > 0 && flagChangeCount.removals > 0 && " / "}
              {flagChangeCount.removals > 0 && `-${flagChangeCount.removals}`}
            </Text>
          )}
        </Flex>
        <InputGroup mb={2}>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search customers..."
            value={customerSearchQuery}
            onChange={e => setCustomerSearchQuery(e.target.value)}
            bg="white"
            size="sm"
            isDisabled={!selectedFlag}
          />
        </InputGroup>

        {/* Fixed height customer list - prevents CLS */}
        <Box h="180px" overflowY="auto" borderWidth="1px" borderRadius="md" bg="white" p={1}>
          {!selectedFlag ? (
            <Flex h="full" alignItems="center" justifyContent="center">
              <Text color="gray.500" fontSize="sm" textAlign="center">
                Select a feature flag first
              </Text>
            </Flex>
          ) : filteredCustomers.length === 0 ? (
            <Flex h="full" alignItems="center" justifyContent="center">
              <Text color="gray.500" fontSize="sm" textAlign="center">
                No customers found
              </Text>
            </Flex>
          ) : (
            <VStack align="stretch" gap={0}>
              {filteredCustomers.map(customer => {
                const isChecked = isCustomerEnabled(customer.cxId);
                const changeState = getCustomerChangeState(customer.cxId);

                return (
                  <Flex
                    key={customer.cxId}
                    px={2}
                    py={1.5}
                    borderRadius="sm"
                    bg={
                      changeState === "addition"
                        ? "green.50"
                        : changeState === "removal"
                        ? "red.50"
                        : undefined
                    }
                    _hover={{
                      bg:
                        changeState === "addition"
                          ? "green.100"
                          : changeState === "removal"
                          ? "red.100"
                          : "gray.50",
                    }}
                    cursor="pointer"
                    onClick={() => handleCustomerToggle(customer.cxId)}
                    alignItems="center"
                  >
                    <Checkbox
                      isChecked={isChecked}
                      onChange={() => handleCustomerToggle(customer.cxId)}
                      mr={2}
                      size="sm"
                      colorScheme={
                        changeState === "addition"
                          ? "green"
                          : changeState === "removal"
                          ? "red"
                          : "blue"
                      }
                    />
                    <Box flex={1} minW={0}>
                      <Flex alignItems="center" gap={2}>
                        <Text fontSize="sm" fontWeight="medium" isTruncated>
                          {customer.name}
                        </Text>
                        {changeState === "addition" && (
                          <Text fontSize="xs" color="green.600" fontWeight="medium">
                            +
                          </Text>
                        )}
                        {changeState === "removal" && (
                          <Text fontSize="xs" color="red.600" fontWeight="medium">
                            −
                          </Text>
                        )}
                      </Flex>
                      <Text fontSize="xs" color="gray.500" isTruncated>
                        {customer.cxId}
                      </Text>
                    </Box>
                  </Flex>
                );
              })}
            </VStack>
          )}
        </Box>
      </Box>
    </VStack>
  );
}
